/**
 * Shared Design Tokens — Semantic Token Contract (Pass 3)
 *
 * Single source of truth for the semantic token layer used by both web
 * (via generated CSS custom properties) and mobile (via JS object access).
 *
 * Pass 3 is the intentional recolor. Values below now match the target spec
 * in design-migration/semantic-tokens.md. Deltas from Pass 2:
 *
 *   - Surfaces neutralized from navy (#1e2a3a family) to cool-neutral gray.
 *   - Accent shifts per-theme (#4f8fe8 dark / #2563c9 light) instead of
 *     identical #3b82f6.
 *   - Info folds into accent-primary; cyan (#06b6d4) is gone from the
 *     accent-info slot. statusColors.*.scheduled stays cyan — that's the
 *     status semantic, not the accent-info semantic.
 *   - Semantics desaturated ("too hot" green/red/warning).
 *   - Light shadows now have real values (Pass 2 seeded 'none').
 *   - Dark status pill trios flip from statusColors.light (Pass 2 echo) to
 *     statusColors.dark (real dark values).
 *   - Tint trios per-theme with target-spec alphas.
 *
 * See design-migration/semantic-tokens.md for the contract this file mirrors.
 */

import { statusColors } from './colors.js'

// --- Status pill trio helper ---
// Takes a { fg, bg } from statusColors and normalizes to { text, bg }.
const pillFromStatus = ({ fg, bg }) => ({ text: fg, bg })

// --- Hex → RGB-triple helper ---
// Converts '#232932' → '35 41 50' (space-separated channels). Used by
// flattenToCssVars to emit `--semantic-bg-*-rgb` vars alongside the hex
// `--semantic-bg-*` vars, so Tailwind's `<alpha-value>` opacity syntax
// can split the color for `bg-{page,surface,elevated,sunken}/N` classes.
// See § BG ALPHA SHAPE in design-migration/semantic-tokens.md.
function hexToRgbTriple(hex) {
  const cleaned = hex.replace(/^#/, '')
  if (cleaned.length !== 6) throw new Error(`hexToRgbTriple: expected 6-digit hex, got ${hex}`)
  const r = parseInt(cleaned.slice(0, 2), 16)
  const g = parseInt(cleaned.slice(2, 4), 16)
  const b = parseInt(cleaned.slice(4, 6), 16)
  return `${r} ${g} ${b}`
}

const statusPillsFromLight = Object.fromEntries(
  Object.entries(statusColors.light).map(([k, v]) => [k, pillFromStatus(v)])
)
const statusPillsFromDark = Object.fromEntries(
  Object.entries(statusColors.dark).map(([k, v]) => [k, pillFromStatus(v)])
)

// --- The contract ---

export const semanticTokens = {
  light: {
    bg: {
      // Also hardcoded as `#12161c` (dark counterpart) in packages/web/src/main.jsx's
      // pre-hydration flash screens (showErrorScreen / showLoadingScreen /
      // showFallbackApp). Those render before CSS vars are available, so a
      // literal is unavoidable. If this value changes, update main.jsx too.
      page: '#f7f8fa',
      surface: '#ffffff',
      elevated: '#ffffff',
      sunken: '#eef1f4',
      hover: 'rgba(16, 20, 26, 0.04)',
      active: 'rgba(16, 20, 26, 0.07)',
      selected: 'rgba(37, 99, 201, 0.10)',
      // Pass 6 chrome-migration 2D-d — solid, theme-adaptive hover for
      // opaque-resting elements where the alpha-baked bg-hover (4% wash) is
      // too subtle. Light value matches the hand-authored L602 override
      // (active:bg-slate-700 → #e2e8f0) — the best pre-existing evidence
      // of intended light-hover appearance for the retargeted sites.
      // See semantic-tokens.md § Elevated hover for consumer guidance.
      elevatedHover: '#e2e8f0',
      // Pass 6 chrome-migration 2F — static neutral fill for tracks,
      // toggle-off states, dividers, and neutral chips. Solid, theme-
      // adaptive. NOT a hover state (see elevatedHover); NOT an overlay
      // (solid, not translucent). Retunable post-eyeball.
      //
      // Naming note: owner-directed role name was `muted`. TWO collisions
      // forced a rename to `mute`:
      //   1. shadcn owns `bg-muted` + `text-muted-foreground` (~20 live
      //      consumers via `muted: { DEFAULT, foreground }` in Tailwind).
      //   2. `neutral` collides with Tailwind's built-in `neutral-{50…950}`
      //      palette (122 consumers). A `neutral: { DEFAULT }` extend does
      //      NOT emit bare `.bg-neutral` because the built-in scale takes
      //      over class emission — verified by Tailwind CLI smoke-test.
      //   `mute` (single word, non-colliding, same root as `muted`)
      //   preserves the reduced-attention role semantic without breaking
      //   either shadcn or the Tailwind scale.
      // See semantic-tokens.md § Mute (neutral fill).
      mute: '#cbd5e1',
      input: '#ffffff',
      inputDisabled: '#f1f4f7',
    },
    text: {
      primary: '#1a1d24',
      secondary: '#475262',
      muted: '#6b7480',
      disabled: '#aeb6c0',
      inverse: '#ffffff',
      link: '#2563c9',
      placeholder: '#8a929c',
      onAccent: '#ffffff',
    },
    border: {
      default: 'rgba(16, 20, 26, 0.10)',
      subtle: 'rgba(16, 20, 26, 0.06)',
      strong: 'rgba(16, 20, 26, 0.16)',
      focus: '#2563c9',
      // Pass 6 chrome-migration 2G — solid, theme-adaptive neutral border for
      // sites where border-{default,subtle,strong} (alpha-baked 5-16%) is too
      // subtle to preserve intent. Primarily replaces `border-slate-{600,700}`
      // solid-line consumers (form-input borders, card outlines) — see 2G
      // scoping handoff § MAPPING PROPOSAL / Border.
      //
      // Deliberately a SEPARATE var from `--semantic-bg-mute` (which shares
      // the same values today) so future retunes can decouple bg-mute and
      // border-mute without cross-effects. Same decoupling rationale that
      // rejected sharing bg-hover with elevated-hover in PR-2D-d.
      //
      // See semantic-tokens.md § Mute (border extension) + § BORDER ALPHA SHAPE.
      mute: '#cbd5e1',
    },
    // Pass 6 chrome-migration 2G — divide utilities need their own slot for the
    // "solid neutral row-separator" idiom that maps ~12 divide-slate-{600,700}
    // sites plus future divide consumers. Same values as border.mute today
    // but SEPARATE var so retunes decouple. See semantic-tokens.md § Mute
    // (divide extension).
    divide: {
      mute: '#cbd5e1',
    },
    accent: {
      primary: '#2563c9',
      success: '#1c8a57',
      warning: '#b5790f',
      danger: '#c5402f',
      info: '#2563c9', // folded into accent-primary
      purple: '#7c3aed', // Pass 6 chrome-migration Substep 1c blocker; 36 live consumers
    },
    tint: {
      accent:  { bg: 'rgba(37, 99, 201, 0.10)', text: '#2563c9', border: 'rgba(37, 99, 201, 0.30)' },
      success: { bg: 'rgba(28, 138, 87, 0.10)', text: '#1c8a57', border: 'rgba(28, 138, 87, 0.30)' },
      warning: { bg: 'rgba(181, 121, 15, 0.12)', text: '#97650c', border: 'rgba(181, 121, 15, 0.30)' },
      danger:  { bg: 'rgba(197, 64, 47, 0.10)',  text: '#c5402f', border: 'rgba(197, 64, 47, 0.30)' },
    },
    shadow: {
      sm:      '0 1px 2px rgba(16, 24, 40, 0.06)',
      default: '0 1px 3px rgba(16, 24, 40, 0.10)',
      md:      '0 4px 12px rgba(16, 24, 40, 0.10)',
      lg:      '0 12px 24px rgba(16, 24, 40, 0.12)',
      xl:      '0 24px 48px rgba(16, 24, 40, 0.14)',
      '2xl':   '0 32px 64px rgba(16, 24, 40, 0.16)',
      inner:   'inset 0 1px 2px rgba(16, 24, 40, 0.06)',
      color:   'rgba(16, 24, 40, 0.10)',
    },
    // Modal scrim (Pass 5 addendum). Slightly less opaque in light mode so
    // the underlying page still reads through — matches the pre-token
    // inline literal preserved from the theme-* retirement.
    scrim: {
      default: 'rgba(0, 0, 0, 0.5)',
    },
    // Overlay tint (Pass 6 chrome-migration 2D-cde). Distinct from `scrim`:
    //   - scrim uses a PURE-BLACK base (rgba(0, 0, 0, N)) for modal backdrops
    //     — a strong dim over the entire viewport.
    //   - overlay uses a SLATE-800 base (rgba(30, 41, 59, N)) for subtle
    //     dark-tinted backings — tab-bar bands, alt-row shading, hover
    //     overlays, grouped-content grouping.
    //   Do not consolidate: scrim is 1 site (modal shell), overlay is ~288
    //   sites across chrome. Different color base + different intent.
    //   Values are IDENTICAL across light and dark themes (the defining
    //   property): dark-tinted overlay renders consistently regardless of
    //   underlying surface theme. Derived from what the pre-migration
    //   `bg-slate-800/N` fallback renders on white bg today.
    overlay: {
      subtle: 'rgba(30, 41, 59, 0.3)',   // 3 strength buckets (2D-cde categorization):
      medium: 'rgba(30, 41, 59, 0.5)',   //   subtle: consolidates prior /10, /20, /30, /40 (~76 sites)
      strong: 'rgba(30, 41, 59, 0.6)',   //   medium: consolidates prior /50 (~152 sites)
    },                                    //   strong: consolidates prior /60, /70, /80, /90 (~55 sites)
    status: statusPillsFromLight,
  },
  dark: {
    bg: {
      // Also hardcoded as `#12161c` in packages/web/src/main.jsx's
      // pre-hydration flash screens (showErrorScreen / showLoadingScreen /
      // showFallbackApp). Those render before CSS vars are available, so a
      // literal is unavoidable. If this value changes, update main.jsx too.
      page: '#12161c',
      surface: '#181d24',
      elevated: '#232932',
      sunken: '#0e1116',
      hover: 'rgba(255, 255, 255, 0.04)',
      active: 'rgba(255, 255, 255, 0.07)',
      selected: 'rgba(79, 143, 232, 0.16)',
      // Pass 6 chrome-migration 2D-d — one visible step lighter than
      // elevated (#232932). Slate-700-adjacent (Tailwind slate-700 is
      // #334155; #2a3140 sits between elevated and slate-700, preserving
      // "raise on hover" without overshooting into pure slate-700). See
      // semantic-tokens.md § Elevated hover for consumer guidance.
      elevatedHover: '#2a3140',
      // Pass 6 chrome-migration 2F — static neutral fill. See light-mode
      // comment for full rationale and naming-collision note. Dark value
      // sits between elevated (#232932) and slate-600 (#475569) —
      // preserves neutral-mid affordance without collapsing into either
      // elevated hue or slate-600 hex.
      mute: '#3a4150',
      input: '#181d24',
      inputDisabled: '#232932',
    },
    text: {
      primary: '#eef1f4',
      secondary: '#b9c0c9',
      muted: '#9aa3ad',
      disabled: '#5a626d',
      inverse: '#12161c',
      link: '#6aa5f0',
      placeholder: '#6b7480',
      onAccent: '#ffffff',
    },
    border: {
      default: 'rgba(255, 255, 255, 0.08)',
      subtle: 'rgba(255, 255, 255, 0.05)',
      strong: 'rgba(255, 255, 255, 0.14)',
      focus: '#4f8fe8',
      // Pass 6 chrome-migration 2G — same intent as light block; see light-side
      // comment for full rationale. Dark value #3a4150 matches bg-mute dark;
      // preserves solid-line character of border-slate-{600,700} for form-input
      // borders + card outlines. SEPARATE var from bg-mute for retune decoupling.
      mute: '#3a4150',
    },
    // Pass 6 chrome-migration 2G — see light-block comment for full rationale.
    divide: {
      mute: '#3a4150',
    },
    accent: {
      primary: '#4f8fe8',
      success: '#46b07e',
      warning: '#d9a33c',
      danger: '#e0685c',
      info: '#4f8fe8', // folded into accent-primary
      purple: '#a78bfa', // Pass 6 chrome-migration Substep 1c blocker; 36 live consumers
    },
    tint: {
      accent:  { bg: 'rgba(79, 143, 232, 0.14)', text: '#6aa5f0', border: 'rgba(79, 143, 232, 0.30)' },
      success: { bg: 'rgba(70, 176, 126, 0.14)', text: '#5fc596', border: 'rgba(70, 176, 126, 0.30)' },
      warning: { bg: 'rgba(217, 163, 60, 0.15)', text: '#e6b860', border: 'rgba(217, 163, 60, 0.30)' },
      danger:  { bg: 'rgba(224, 104, 92, 0.14)', text: '#ec8579', border: 'rgba(224, 104, 92, 0.30)' },
    },
    shadow: {
      sm:      '0 1px 2px rgba(0, 0, 0, 0.40)',
      default: '0 1px 3px rgba(0, 0, 0, 0.50)',
      md:      '0 4px 10px rgba(0, 0, 0, 0.50)',
      lg:      '0 10px 22px rgba(0, 0, 0, 0.55)',
      xl:      '0 20px 40px rgba(0, 0, 0, 0.60)',
      '2xl':   '0 32px 64px rgba(0, 0, 0, 0.65)',
      inner:   'inset 0 1px 2px rgba(0, 0, 0, 0.40)',
      color:   'rgba(0, 0, 0, 0.50)',
    },
    // Modal scrim (Pass 5 addendum). Heavier on dark to keep the surface
    // beneath the modal from bleeding through — matches the pre-token
    // inline literal preserved from the theme-* retirement.
    scrim: {
      default: 'rgba(0, 0, 0, 0.7)',
    },
    // Overlay tint — same values as light. Constant-across-themes is the
    // defining property; see the light-mode comment for full rationale.
    overlay: {
      subtle: 'rgba(30, 41, 59, 0.3)',
      medium: 'rgba(30, 41, 59, 0.5)',
      strong: 'rgba(30, 41, 59, 0.6)',
    },
    status: statusPillsFromDark,
  },
}

// --- Flattener: token object → [[cssVarName, value], ...] ---

/**
 * Flatten a mode ('light' | 'dark') to the CSS custom property names + values
 * currently expected by styles.css and by Tailwind consumers.
 *
 * The name mapping is deliberately explicit rather than mechanically derived
 * from key paths — the current CSS naming is inconsistent (bg.input →
 * --semantic-bg-input, tint.success.bg → --semantic-bg-success-subtle) and
 * a fragile mechanical mapping would obscure that.
 */
export function flattenToCssVars(mode) {
  const t = semanticTokens[mode]
  const vars = []

  vars.push(
    ['--semantic-bg-page', t.bg.page],
    ['--semantic-bg-surface', t.bg.surface],
    ['--semantic-bg-elevated', t.bg.elevated],
    ['--semantic-bg-sunken', t.bg.sunken],
    ['--semantic-bg-hover', t.bg.hover],
    ['--semantic-bg-active', t.bg.active],
    ['--semantic-bg-selected', t.bg.selected],
    ['--semantic-bg-elevated-hover', t.bg.elevatedHover],
    ['--semantic-bg-mute', t.bg.mute],
    ['--semantic-bg-input', t.bg.input],
    ['--semantic-bg-input-disabled', t.bg.inputDisabled],

    // RGB-triple companions for the solid-hex bg + accent slots. Tailwind's
    // `theme.extend.colors` with a plain `var(--x)` value CANNOT split the
    // color for `/N` opacity — the `bg-elevated/50` class silently no-ops.
    // With `rgb(var(--x-rgb) / <alpha-value>)` in the config, the `/N`
    // modifier compiles correctly.
    //
    // Emit RGB-triples for solid-hex tokens only:
    //   - bg.{page,surface,elevated,sunken}: solid hex, need /N support
    //     for translucent overlays over the base surface.
    //   - accent.{primary,success,warning,danger,info,purple}: solid hex,
    //     need /N support for translucent-tint chip/pill backings
    //     (bg-accent-primary/20 etc, ~40 pre-existing usages).
    //
    // Not reshaped:
    //   - bg.{hover,active,selected}: already alpha-baked rgba (no /N sense).
    //   - bg.{input,inputDisabled}: emitted via addUtilities, needs separate
    //     path (follow-up).
    //   - border, text (except accent): emitted via addUtilities, follow-up.
    ['--semantic-bg-page-rgb', hexToRgbTriple(t.bg.page)],
    ['--semantic-bg-surface-rgb', hexToRgbTriple(t.bg.surface)],
    ['--semantic-bg-elevated-rgb', hexToRgbTriple(t.bg.elevated)],
    ['--semantic-bg-sunken-rgb', hexToRgbTriple(t.bg.sunken)],
    ['--semantic-accent-primary-rgb', hexToRgbTriple(t.accent.primary)],
    ['--semantic-accent-success-rgb', hexToRgbTriple(t.accent.success)],
    ['--semantic-accent-warning-rgb', hexToRgbTriple(t.accent.warning)],
    ['--semantic-accent-danger-rgb', hexToRgbTriple(t.accent.danger)],
    ['--semantic-accent-info-rgb', hexToRgbTriple(t.accent.info)],
    ['--semantic-accent-purple-rgb', hexToRgbTriple(t.accent.purple)],

    ['--semantic-text-primary', t.text.primary],
    ['--semantic-text-secondary', t.text.secondary],
    ['--semantic-text-muted', t.text.muted],
    ['--semantic-text-disabled', t.text.disabled],
    ['--semantic-text-inverse', t.text.inverse],
    ['--semantic-text-link', t.text.link],
    ['--semantic-text-placeholder', t.text.placeholder],
    ['--semantic-text-on-accent', t.text.onAccent],

    ['--semantic-border-default', t.border.default],
    ['--semantic-border-subtle', t.border.subtle],
    ['--semantic-border-strong', t.border.strong],
    ['--semantic-border-focus', t.border.focus],
    // Pass 6 chrome-migration 2G — solid neutral border slot (not alpha-baked).
    // Bare `.border-mute` compiles to this var. See semantic-tokens.md § Mute.
    ['--semantic-border-mute', t.border.mute],
    // Underlying "color" component (without alpha) of the alpha-baked border
    // slots, as an RGB triple. Enables the enumerated `.border-{default,
    // subtle,strong}/N` override utilities (Pass 6 chrome-migration 2G) to
    // emit `rgb(var(--semantic-border-color-rgb) / 0.N)` with theme-swapped
    // base color. Bare `.border-{default,subtle,strong}` stays alpha-baked
    // via the pre-existing var refs — this triple is /N-only.
    // See semantic-tokens.md § BORDER ALPHA SHAPE.
    ['--semantic-border-color-rgb', mode === 'dark' ? '255 255 255' : '16 20 26'],
    // border-mute is solid hex, so its rgb-triple comes from the hex directly.
    // Enables `.border-mute/N` override utilities.
    ['--semantic-border-mute-rgb', hexToRgbTriple(t.border.mute)],
    // Divide-mute — solid neutral row-separator. SEPARATE var from
    // border-mute for retune decoupling; identical values today.
    ['--semantic-divide-mute', t.divide.mute],

    ['--semantic-accent-primary', t.accent.primary],
    ['--semantic-accent-success', t.accent.success],
    ['--semantic-accent-warning', t.accent.warning],
    ['--semantic-accent-danger', t.accent.danger],
    ['--semantic-accent-info', t.accent.info],
    ['--semantic-accent-purple', t.accent.purple],
  )

  // Tint trios — target-spec naming: bg-*-subtle / text-* / border-*.
  for (const [name, tri] of Object.entries(t.tint)) {
    vars.push(
      [`--semantic-bg-${name}-subtle`, tri.bg],
      [`--semantic-text-${name}`, tri.text],
      [`--semantic-border-${name}`, tri.border],
    )
  }

  vars.push(
    ['--semantic-shadow-sm', t.shadow.sm],
    ['--semantic-shadow-default', t.shadow.default],
    ['--semantic-shadow-md', t.shadow.md],
    ['--semantic-shadow-lg', t.shadow.lg],
    ['--semantic-shadow-xl', t.shadow.xl],
    ['--semantic-shadow-2xl', t.shadow['2xl']],
    ['--semantic-shadow-inner', t.shadow.inner],
    ['--semantic-shadow-color', t.shadow.color],

    ['--semantic-scrim-default', t.scrim.default],

    ['--semantic-bg-overlay-subtle', t.overlay.subtle],
    ['--semantic-bg-overlay-medium', t.overlay.medium],
    ['--semantic-bg-overlay-strong', t.overlay.strong],
  )

  for (const [name, pill] of Object.entries(t.status)) {
    vars.push(
      [`--semantic-status-${name}-bg`, pill.bg],
      [`--semantic-status-${name}-text`, pill.text],
    )
  }

  return vars
}

// Convenience: `{ '--semantic-bg-page': '#f7f8fa', ... }`
export function toCssVarObject(mode) {
  return Object.fromEntries(flattenToCssVars(mode))
}
